/**
 * Google Cloud Translation API Provider
 * Free tier: 500,000 characters per month
 * Pricing: $20 per 1M characters after free tier
 */

import { MTProvider } from "./mtEngine";
import { ApiRateLimiter } from "./apiRateLimiter";
import { mtFetch } from "@/lib/mtClient";

const LANG_NAME_TO_CODE: Record<string, string> = {
    english: "en",
    afrikaans: "af",
    portuguese: "pt",
    french: "fr",
    spanish: "es",
    german: "de",
    dutch: "nl",
    italian: "it",
    arabic: "ar",
    chinese: "zh",
    japanese: "ja",
    korean: "ko",
    russian: "ru",
    polish: "pl",
    swedish: "sv",
    norwegian: "no",
    danish: "da",
    finnish: "fi",
    czech: "cs",
    hungarian: "hu",
    romanian: "ro",
    greek: "el",
    turkish: "tr",
    hebrew: "he",
    hindi: "hi",
    thai: "th",
    indonesian: "id",
    malay: "ms",
    swahili: "sw",
    zulu: "zu",
    xhosa: "xh",
    sotho: "st",
    tswana: "tn",
    venda: "ve",
    tsonga: "ts",
    ndebele: "nr",
};

const normaliseCode = (lang: string): string => {
    if (!lang) return lang;
    if (lang.length <= 5 && !lang.includes(" ")) return lang.toLowerCase();
    return LANG_NAME_TO_CODE[lang.toLowerCase()] ?? lang.toLowerCase();
};

class GoogleTranslateProvider implements MTProvider {
    private rateLimiter: ApiRateLimiter;
    // Requests go through our API, which holds the Google key and counts usage.
    private readonly API_ENDPOINT = `${(import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com"}/api/translate/google`;
    private readonly MONTHLY_LIMIT = 500000; // 500k characters free
    private readonly BATCH_SIZE = 128; // Max texts per request

    constructor(_apiKey?: string) {
        this.rateLimiter = new ApiRateLimiter(
            "google_translate",
            this.MONTHLY_LIMIT,
        );
    }

    getName(): string {
        return "Google Translate";
    }

    isAvailable(): boolean {
        // Requests go through our API, which holds the key.
        return true;
    }

    getUsage(): number {
        return this.rateLimiter.getUsage();
    }

    getMonthlyLimit(): number {
        return this.MONTHLY_LIMIT;
    }

    resetUsage(): void {
        this.rateLimiter.resetUsage();
    }

    /**
     * Translate a single text
     */
    async translate(
        text: string,
        sourceLang: string,
        targetLang: string,
    ): Promise<string> {
        if (!text || text.trim().length === 0) {
            return "";
        }

        // Check rate limit
        const charCount = text.length;
        if (!this.rateLimiter.canMakeRequest(charCount)) {
            throw new Error(
                `Rate limit exceeded. Used: ${this.getUsage()}/${this.MONTHLY_LIMIT} chars this month.`,
            );
        }

        try {
            const response = await mtFetch(this.API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    text,
                    source: normaliseCode(sourceLang).split("-")[0],
                    target: normaliseCode(targetLang).split("-")[0],
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    (typeof errorData.error === "string"
                        ? errorData.error
                        : errorData.error?.message) ||
                        `HTTP ${response.status}: ${response.statusText}`,
                );
            }

            const data = await response.json();

            if (!data.data?.translations?.[0]?.translatedText) {
                throw new Error(
                    "Invalid response format from Google Translate",
                );
            }

            const translation = this.decodeHtmlEntities(
                data.data.translations[0].translatedText,
            );

            // Track usage
            this.rateLimiter.trackUsage(charCount);

            return translation;
        } catch (error) {
            console.error("Google Translate error:", error);
            throw new Error(
                error instanceof Error ? error.message : "Translation failed",
            );
        }
    }

    /**
     * Translate multiple texts in batch
     */
    async translateBatch(
        texts: string[],
        sourceLang: string,
        targetLang: string,
    ): Promise<string[]> {
        if (texts.length === 0) {
            return [];
        }

        // Filter out empty texts
        const validTexts = texts.filter((t) => t && t.trim().length > 0);
        if (validTexts.length === 0) {
            return texts.map(() => "");
        }

        // Check rate limit for total characters
        const totalChars = validTexts.join("").length;
        if (!this.rateLimiter.canMakeRequest(totalChars)) {
            throw new Error(
                `Rate limit exceeded. Used: ${this.getUsage()}/${this.MONTHLY_LIMIT} chars this month.`,
            );
        }

        try {
            // Google supports up to 128 texts per request
            const batches: string[][] = [];
            for (let i = 0; i < validTexts.length; i += this.BATCH_SIZE) {
                batches.push(validTexts.slice(i, i + this.BATCH_SIZE));
            }

            const allTranslations: string[] = [];

            for (const batch of batches) {
                const response = await mtFetch(this.API_ENDPOINT, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        text: batch,
                        source: normaliseCode(sourceLang).split("-")[0],
                        target: normaliseCode(targetLang).split("-")[0],
                    }),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(
                        (typeof errorData.error === "string"
                            ? errorData.error
                            : errorData.error?.message) ||
                            `HTTP ${response.status}`,
                    );
                }

                const data = await response.json();
                const translations = data.data.translations.map((t: any) =>
                    this.decodeHtmlEntities(t.translatedText),
                );

                allTranslations.push(...translations);
            }

            // Track usage
            this.rateLimiter.trackUsage(totalChars);

            return allTranslations;
        } catch (error) {
            console.error("Google Translate batch error:", error);
            throw new Error(
                error instanceof Error
                    ? error.message
                    : "Batch translation failed",
            );
        }
    }

    /**
     * Normalize language codes for Google Translate API
     * Google uses standard ISO 639-1 codes
     */
    private normalizeLanguageCode(code: string): string {
        const normalized = code.toLowerCase().replace("_", "-");

        // Handle special cases
        const codeMap: Record<string, string> = {
            "pt-br": "pt",
            "zh-cn": "zh-CN",
            "zh-tw": "zh-TW",
            "en-us": "en",
            "en-gb": "en",
        };

        return codeMap[normalized] || normalized.split("-")[0];
    }

    /**
     * Decode HTML entities that Google Translate sometimes returns
     */
    private decodeHtmlEntities(text: string): string {
        // In HTML mode, only decode non-structural entities
        // Keep <, > intact so HTML tags are preserved
        return text
            .replace(/&amp;/g, "&")
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, " ");
    }
}

export default GoogleTranslateProvider;
